// LandPrepPopup.jsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  IconButton,
  Grid,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  Typography,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";
import Divider from "@mui/material/Divider";
import AddIcon from "@mui/icons-material/Add";
import instance from "../../../globals/axios";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import EditIcon from "../../../assets/svg/EditIcon.svg";
import SuccessPopup from "./SuccessPopup";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(0),
    right: theme.spacing(1),
    fontSize: 16,
  },
  dialogContent: {
    height: 400,
  },
  datepicker: {
    display: "none",
  },
  taxtLabel: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
    marginTop: "10px",
  },
  taxtTitle: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "700 !important",
    color: "#000",
  },
}));

const WeedicidePopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [comment, setComment] = useState("");
  const { showAlert } = useAlert();
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [chemicalOptions, setChemicalOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errors, setErrors] = useState({
    selectedDate: "",
    formDataSet: [],
  });

  let farmerPlotId = getFarmerPlotId();

  const scrollableContainerStyle = {
    maxHeight: "300px",
    overflowY: "auto",
  };
  const [formDataSet, setFormDataSet] = useState([
    {
      chemicalName: "",
      unit: "",
      dosePerAcre: "",
    },
  ]);

  useEffect(() => {
    const fetchChemicalOptions = async () => {
      try {
        const chemicalResponse = await instance.get(
          "plotengagement/get-chemical-names?stage=WEEDICIDE"
        );
        const chemicalOptions = chemicalResponse.data.map(
          (chemical) => chemical.name
        );

        const unitResponse = await instance.get(
          "plotengagement/get-units?stage=WEEDICIDE"
        );
        const unitOptions = unitResponse.data.map((unit) => unit.name);
        setChemicalOptions(chemicalOptions);
        setUnitOptions(unitOptions);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchChemicalOptions();
  }, []);
  const rows = [
    {
      label: t("Chemical Name"),
      type: "dropdown",
      options: chemicalOptions,
      key: "chemicalName",
    },
    { label: t("Unit"), type: "dropdown", options: unitOptions, key: "unit" },
    { label: t("Dose per acre"), type: "textfield", key: "dosePerAcre" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await instance(
          `plotengagement/get-weedicide?farmer_plot=${farmerPlotId}`
        );
        let data = response.data;
        if (data.length > 0) {
          setExistingData(data[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (farmerPlotId) {
      fetchData();
    }
  }, [farmerPlotId]);

  useEffect(() => {
    if (existingData) {
      setSelectedDate(new Date(existingData.application_date));
      const existingChemicalDoses = existingData.chemical_dose || [];
      const updatedFormData = existingChemicalDoses.map((dose) => ({
        chemicalName: dose.chemical_name || "",
        dosePerAcre: dose.dose_per_acre || "",
        unit: dose.unit || "",
      }));
      setFormDataSet(updatedFormData);
      setComment(existingData.comments);
    }
  }, [existingData]);
  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };
  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setErrors((prevErrors) => ({
      ...prevErrors,
      selectedDate: "", // Clear the error for selectedDate field
    }));
  };
  const handleChange = (field, value, index) => {
    setFormDataSet((prevData) => {
      const newData = [...prevData];
      newData[index] = {
        ...newData[index],
        [field]: value,
      };
      return newData;
    });
    setErrors((prevErrors) => {
      const newErrors = [...prevErrors.formDataSet];
      newErrors[index] = {
        ...newErrors[index],
        [field]: "", // Clear the error for the changed field
      };
      return {
        ...prevErrors,
        formDataSet: newErrors,
      };
    });
  };
  const handleAddMore = () => {
    const newFormData = {
      chemicalName: "",
      unit: "",
      dosePerAcre: "",
    };
    setFormDataSet((prevData) => [...prevData, newFormData]);
  };
  const handleDeleteForm = (index) => {
    setFormDataSet((prevData) => {
      const newData = [...prevData];
      newData.splice(index, 1);
      return newData;
    });
  };
  const validateFields = () => {
    let formValid = true;
    let updatedErrors = { ...errors };

    if (!selectedDate) {
      updatedErrors = {
        ...updatedErrors,
        selectedDate: "Date  is required",
      };
      formValid = false;
    }

    const fieldErrors = formDataSet.map((formData) => {
      let fieldError = {};

      if (!formData.chemicalName) {
        fieldError = {
          ...fieldError,
          chemicalName: "Please select a chemical name",
        };
        formValid = false;
      }

      if (!formData.unit) {
        fieldError = {
          ...fieldError,
          unit: "Please select a unit",
        };
        formValid = false;
      }

      if (!formData.dosePerAcre) {
        fieldError = {
          ...fieldError,
          dosePerAcre: "Dose per acre is required",
        };
        formValid = false;
      }

      return fieldError;
    });

    updatedErrors = {
      ...updatedErrors,
      formDataSet: fieldErrors,
    };

    setErrors(updatedErrors);

    return formValid;
  };
  const handleSubmit = () => {
    const isValid = validateFields();
    if (isValid) {
      const formatDate = (date) => {
        const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
        return date
          ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
              date.getDate()
            )}`
          : null;
      };
      const formData = {
        chemical_dose: formDataSet
          .map((item) => ({
            chemical_name: item["chemicalName"],
            unit: item.unit,
            dose_per_acre: item["dosePerAcre"],
          }))
          .filter(
            (item) => item.chemical_name && item.dose_per_acre && item.unit
          ),

        application_date: selectedDate ? formatDate(selectedDate) : null,
        comments: comment,
      };
      if (isEditMode) {
        formData.id = existingData.id;
      } else {
        formData.farmer_plot = farmerPlotId;
      }
      const apiUrl = isEditMode
        ? "plotengagement/update-weedicide"
        : "plotengagement/create-weedicide";
      instance
        .post(apiUrl, formData)
        .then((res) => {
          if (res?.data) {
            setShowSuccessPopup(true);
            setTimeout(() => {
              setShowSuccessPopup(false);
              onClose();
            }, 2000);
          }
        })
        .catch((err) => {
          console.error("Error", err);
          showAlert("error", "Some Error Occured");
        });
    }
  };
  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        PaperProps={{ style: { maxHeight: "calc(100% - 25%)" } }}
      >
        <div style={{ padding: "16px" }}>
          <Grid container>
            <Grid item xs={10}>
              <DialogTitle style={{ padding: "0px" }}>
                {t("Weedicide application")}
              </DialogTitle>
            </Grid>
            {existingData !== null ? (
              <Grid item xs={1} style={{ marginTop: "8px" }}>
                <img src={EditIcon} onClick={handleEditClick} />
              </Grid>
            ) : null}
            <Grid item xs={1}>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={7} style={{ marginTop: "20px" }}>
              <InputLabel style={{ fontSize: "12px" }}>
                {t("Date of application")}
              </InputLabel>
            </Grid>

            <Grid item xs={5}>
              <TextField
                placeholder="DD/MM/YYYY"
                fullWidth
                helperText={errors.selectedDate}
                error={!!errors.selectedDate}
                disabled={!isEditMode && existingData !== null}
                value={selectedDate ? selectedDate.toLocaleDateString() : ""}
                InputProps={{
                  sx: {
                    height: "40px",
                    borderRadius: "5px", // You can adjust this if needed
                    marginTop: "10px",
                    background: "#FFF",
                    border: "1px solid #C7BDBD",
                  },
                  readOnly: true,
                  onClick: () => document.getElementById("date-picker").click(),
                }}
              />
            </Grid>
            <DatePicker
              id="date-picker"
              dateFormat="dd/MM/yyyy"
              selected={selectedDate}
              onChange={handleDateChange}
              className={classes.datepicker}
            />
          </Grid>
          <div style={{ marginTop: "10px" }}>
            <Typography className={classes.taxtTitle}>
              {t("Chemical Dose")}
            </Typography>
          </div>
          <div style={{ ...scrollableContainerStyle }}>
            {formDataSet.map((formData, formIndex) => (
              <div key={formIndex} style={{ padding: "0px" }}>
                {rows.map((row, index) => (
                  <Grid key={index} container style={{ marginTop: "10px" }}>
                    <Grid item xs={6}>
                      <InputLabel className={classes.taxtLabel}>
                        {row.label}
                      </InputLabel>
                    </Grid>
                    <Grid item xs={6}>
                      {row.type === "dropdown" ? (
                        <>
                          <Select
                            disabled={!isEditMode && existingData !== null}
                            value={formData[row.key]}
                            error={
                              errors.formDataSet[formIndex] &&
                              errors.formDataSet[formIndex][row.key]
                                ? true
                                : false
                            }
                            helperText={
                              errors.formDataSet[formIndex] &&
                              errors.formDataSet[formIndex][row.key]
                            }
                            onChange={(event) =>
                              handleChange(
                                row.key,
                                event.target.value,
                                formIndex
                              )
                            }
                            style={{ width: "100%" }}
                            sx={{
                              height: "40px", // Set the desired height
                              border: "1px solid #9C9FA0",
                              borderRadius: "5px",
                            }}
                          >
                            {row.options.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.formDataSet[formIndex] && (
                            <div>
                              {row.key === "chemicalName" &&
                                errors.formDataSet[formIndex].chemicalName && (
                                  <Typography variant="caption" color="error">
                                    {errors.formDataSet[formIndex].chemicalName}
                                  </Typography>
                                )}
                              {row.key === "unit" &&
                                errors.formDataSet[formIndex].unit && (
                                  <Typography variant="caption" color="error">
                                    {errors.formDataSet[formIndex].unit}
                                  </Typography>
                                )}
                            </div>
                          )}
                        </>
                      ) : (
                        <TextField
                          type="number"
                          disabled={!isEditMode && existingData !== null}
                          helperText={
                            errors.formDataSet[formIndex] &&
                            errors.formDataSet[formIndex][row.key]
                          }
                          error={
                            errors.formDataSet[formIndex] &&
                            errors.formDataSet[formIndex][row.key]
                              ? true
                              : false
                          }
                          value={formData[row.key]}
                          onChange={(event) =>
                            handleChange(row.key, event.target.value, formIndex)
                          }
                          style={{ width: "100%" }}
                          size="small"
                          InputProps={{
                            sx: {
                              height: "40px",
                              borderRadius: "5px", // You can adjust this if needed
                              marginTop: "10px",
                              background: "#FFF",
                              border: "1px solid #C7BDBD",
                            },
                          }}
                        />
                      )}
                    </Grid>
                  </Grid>
                ))}

                {formIndex < formDataSet.length - 1 && (
                  <div>
                    <Divider style={{ margin: "10px 0" }} />
                    {isEditMode && existingData !== null ? (
                      <Grid container>
                        <Grid item xs={10}></Grid>
                        <Grid item xs={2}>
                          <IconButton
                            aria-label="delete"
                            onClick={() =>
                              handleDeleteForm(
                                !isEditMode ? formIndex + 1 : formIndex + 1
                              )
                            }
                          >
                            <CloseIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ) : existingData === null ? (
                      <Grid container>
                        <Grid item xs={10}></Grid>
                        <Grid item xs={2}>
                          <IconButton
                            aria-label="delete"
                            onClick={() =>
                              handleDeleteForm(
                                !isEditMode ? formIndex + 1 : formIndex + 1
                              )
                            }
                          >
                            <CloseIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <DialogActions style={{ display: "grid" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
              marginRight: "16px",
              width: "140px",
            }}
          >
            <Button
              onClick={handleAddMore}
              style={{
                background:
                  !isEditMode && existingData !== null
                    ? "rgba(111, 163, 78, 0.5)"
                    : "#6FA34E",
                border: "#538434",
                borderRadius: "10px",
                color: "#FFF",
                textTransform: "none",
              }}
              disabled={!isEditMode && existingData !== null}
            >
              <AddIcon style={{ marginRight: "5px", fontSize: "15px" }} />{" "}
              {t("Add More")}
            </Button>
          </div>
        </DialogActions>
        <div style={{ display: "grid", padding: "10px" }}>
          <InputLabel>{t("Comment (Optional)")}</InputLabel>
          <TextField
            multiline
            rows={1}
            value={comment}
            onChange={handleCommentChange}
            disabled={!isEditMode && existingData !== null}
          />
        </div>

        <div
          style={{ display: "flex", margin: "auto", justifyContent: "center" }}
        >
          {existingData && isEditMode ? (
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
                width: "140px",
              }}
            >
              <CustomButton onClick={handleSubmit} hideIcon>
                {t("Update")}
              </CustomButton>
            </div>
          ) : !existingData && !isEditMode ? (
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
                width: "140px",
              }}
            >
              <CustomButton onClick={handleSubmit} hideIcon>
                {t("Submit")}
              </CustomButton>
            </div>
          ) : null}
        </div>
        {showSuccessPopup && (
          <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
        )}
      </Dialog>
    </div>
  );
};

export default WeedicidePopup;
